import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { firstValueFrom, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import {
  Brand,
  Product,
  Image,
  ExportDoc,
  ProductAddBarcodeDto,
  ProductRemoveBarcodeDto,
  ProductSearchDto,
  ProductCreateDto,
  ProductUpdateDto,
  ProductRemoveAnalogueDto,
  ProductAddAnalogueDto,
  UploadImageDto,
  RemoveDouble,
  ProductAddFolderDto,
  ProductFolderBulkDto,
  MovingDoc,
  ReturnToProviderDoc,
  ImportDoc,
  ReturnToWarehouseDoc,
  Order,
  ArticleCreateDto,
  Article,
  ArticleUpdateDto,
  ArticleSearchDto,
  UploadNewProductsFile,
  ToggleSearchKey,
  SearchKey,
  RemoveDoubleResult,
  ProductMergeCard,
  ProductMergePreview,
  UpdateRetailPriceDto,
  UnifiedProductMovementItem,
  UnifiedProductMovementResponse,
  ProductImportFromExistRequest,
  ProductImportFromExistResult,
  ProductAnalogueManualUpsertDto,
  ProductAnalogueSupplierExcludeDto,
  ProductAnalogueSupplierLoadDto,
  ProductAnaloguesV2Response,
  ProductAnalogueSupplierSource,
  ProductAnalogueSyncStatus
} from 'src/typings';
import { CurrentUserService as UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private socket: Socket,
    private userService: UserService
  ) {}

  productGw = this.socket.fromEvent<Product[]>('product_update');

  analgogueGw = this.socket.fromEvent<{ product_id: number; list: Product[] }>('analogies_updated');

  analgogueV2Gw = this.socket.fromEvent<{ productIds: number[] }>('analogies_updated_v2');

  folderAddedToProduct = this.socket.fromEvent<{ productId: number; folderId: number }>(
    'folder_added_to_product'
  );

  folderRemovedFromProduct = this.socket.fromEvent<{ productId: number; folderId: number }>(
    'folder_removed_from_product'
  );

  barcodeAddedToProduct = this.socket.fromEvent<{ productId: number; barcode: string }>(
    'product_added_barcode'
  );

  barcodeRemovedFromProduct = this.socket.fromEvent<{ productId: number; barcode: string }>(
    'product_removed_barcode'
  );

  addSearchKeyGW = this.socket.fromEvent<{ productId: number; key: string }>('add_search_key');

  removeSearchKeyGW = this.socket.fromEvent<{ productId: number; key: string }>(
    'remove_search_key'
  );

  addArticleGW = this.socket.fromEvent<{ productId: number; article: Article }>('article_added');

  removeArticleGW = this.socket.fromEvent<{ productId: number; article: Article }>(
    'article_removed'
  );

  getList() {
    return this.http.get<Product[]>(`${environment.BACKEND}/product/list`);
  }

  addBarcodeToProduct(payload: ProductAddBarcodeDto): Observable<
    Array<{
      id: number;
      barcode: string;
      shared_products?: Array<{ id: number; description: string; article: string; brand: string }>;
    }>
  > {
    return this.http.put<
      Array<{
        id: number;
        barcode: string;
        shared_products?: Array<{
          id: number;
          description: string;
          article: string;
          brand: string;
        }>;
      }>
    >(`${environment.BACKEND}/product/add_barcode`, payload);
  }

  removeBarcodeToProduct(payload: ProductRemoveBarcodeDto): Observable<
    Array<{
      id: number;
      barcode: string;
      shared_products?: Array<{ id: number; description: string; article: string; brand: string }>;
    }>
  > {
    return this.http.put<
      Array<{
        id: number;
        barcode: string;
        shared_products?: Array<{
          id: number;
          description: string;
          article: string;
          brand: string;
        }>;
      }>
    >(`${environment.BACKEND}/product/remove_barcode`, payload);
  }

  search(payload: ProductSearchDto) {
    const resp = this.http
      .post<{
        data: Product[];
        length: number;
        brand_list: Brand[];
        min_retail: number;
        max_retail: number;
      }>(`${environment.BACKEND}/product/search`, payload, {
        observe: 'body'
      })
      .pipe(
        map((response) => {
          response.data.map((prod) => {
            if (!prod.lastProviders) return;
            prod.lastProviders.map((provider) => {
              provider.date = new Date(provider.date);
            });
            return prod;
          });
          return response;
        })
      );
    return resp;
  }

  create(payload: ProductCreateDto) {
    return this.http.post<Product>(`${environment.BACKEND}/product/create`, payload);
  }

  updateProduct(payload: ProductUpdateDto) {
    const resp = this.http.put<Product>(`${environment.BACKEND}/product/update`, payload);
    return resp;
  }

  removeAnalogue(payload: ProductRemoveAnalogueDto) {
    const resp = this.http.put<Product[]>(
      `${environment.BACKEND}/product/remove_analogue`,
      payload
    );
    return resp;
  }

  addAnalogue(payload: ProductAddAnalogueDto) {
    return this.http.put<Product[]>(`${environment.BACKEND}/product/add_analogue`, payload);
  }

  getAnalogues(id: number) {
    return this.http.get<Product[]>(`${environment.BACKEND}/product/analogues/${id}`).pipe(
      map((list: Product[]) => {
        return list.map((prod) => {
          prod.stock.map((wh) => {
            wh.quan = Number(wh.quan);
          });
          return prod;
        });
      })
    );
  }

  getAnaloguesV2(id: number, contractIds: number[] = []) {
    let params = new HttpParams();
    if (contractIds.length) {
      params = params.set('contractIds', contractIds.join(','));
    }
    return this.http.get<ProductAnaloguesV2Response>(
      `${environment.BACKEND}/product/analogues/v2/${id}`,
      {
        params
      }
    );
  }

  addManualAnalogueV2(payload: ProductAnalogueManualUpsertDto) {
    return this.http.post<ProductAnaloguesV2Response>(
      `${environment.BACKEND}/product/analogues/v2/manual`,
      payload
    );
  }

  removeManualAnalogueV2(payload: ProductAnalogueManualUpsertDto) {
    return this.http.request<ProductAnaloguesV2Response>(
      'delete',
      `${environment.BACKEND}/product/analogues/v2/manual`,
      {
        body: payload
      }
    );
  }

  loadSupplierAnaloguesV2(payload: ProductAnalogueSupplierLoadDto) {
    return this.http.post<ProductAnaloguesV2Response>(
      `${environment.BACKEND}/product/analogues/v2/supplier-load`,
      payload
    );
  }

  excludeSupplierAnalogueV2(payload: ProductAnalogueSupplierExcludeDto) {
    return this.http.request<ProductAnaloguesV2Response>(
      'delete',
      `${environment.BACKEND}/product/analogues/v2/supplier`,
      {
        body: payload
      }
    );
  }

  getSupplierAnalogueSourcesCurrentFirm() {
    return this.http.get<ProductAnalogueSupplierSource[]>(
      `${environment.BACKEND}/supplier-analogue-sync/sources/current-firm`
    );
  }

  getSupplierAnalogueSyncStatusCurrentFirm() {
    return this.http.get<ProductAnalogueSyncStatus[]>(
      `${environment.BACKEND}/supplier-analogue-sync/status/current-firm`
    );
  }

  runSupplierAnalogueSync(contractId: number) {
    return this.http.post(
      `${environment.BACKEND}/supplier-analogue-sync/contract/${contractId}/run`,
      {}
    );
  }

  removeImage(image: Image) {
    return this.http.post<string>(`${environment.BACKEND}/image/remove_image`, {
      name: image.name
    });
  }

  setPrimaryImage(productId: number, imageId: number): Observable<Product> {
    return this.http.put<Product>(
      `${environment.BACKEND}/product/${productId}/images/${imageId}/primary`,
      {}
    );
  }

  removeProductImage(productId: number, imageId: number): Observable<Product> {
    return this.http.delete<Product>(`${environment.BACKEND}/product/${productId}/images/${imageId}`);
  }

  saveImage(payload: UploadImageDto) {
    return this.http.post<Product>(`${environment.BACKEND}/product/upload_image`, payload);
  }

  async uploadImage(file: File, product_id: number) {
    const imageName = await firstValueFrom(this.userService.uploadFile(file, product_id));
    return this.saveImage({ product_id, image: imageName.name });
  }

  deleteProduct(product_id: number) {
    return this.http.delete(`${environment.BACKEND}/product/${product_id}`);
  }

  previewMerge(payload: RemoveDouble) {
    return this.http.post<ProductMergePreview>(`${environment.BACKEND}/product/merge_preview`, payload);
  }
  mergeCandidates(query: string) {
    return this.http.get<ProductMergeCard[]>(`${environment.BACKEND}/product/merge_candidates`, { params: { q: query } });
  }
  removeDouble(payload: RemoveDouble) {
    return this.http.post<RemoveDoubleResult>(
      `${environment.BACKEND}/product/remove_double`,
      payload
    );
  }

  addImage(payload: { productId: number; url: string }): Promise<Product[]> {
    const { productId, url } = payload;
    const urls = url.split(';');
    return Promise.all(
      urls.map(async (link) => {
        const name = await firstValueFrom(
          this.http.post<{ name: string }>(`${environment.BACKEND}/image/upload_url`, {
            url: link
          })
        );

        return firstValueFrom(
          this.http.post<Product>(`${environment.BACKEND}/product/upload_image`, {
            product_id: productId,
            image: name.name,
            url: link
          })
        );
      })
    );
  }

  addFolder(payload: ProductAddFolderDto) {
    return this.http.put<Product>(`${environment.BACKEND}/product/add_folder`, payload);
  }

  removeFolder(payload: ProductAddFolderDto) {
    return this.http.put<Product>(`${environment.BACKEND}/product/remove_folder`, payload);
  }

  addFolderBulk(payload: ProductFolderBulkDto) {
    return this.http.put<void>(`${environment.BACKEND}/product/add_folder_bulk`, payload);
  }

  removeFolderBulk(payload: ProductFolderBulkDto) {
    return this.http.put<void>(`${environment.BACKEND}/product/remove_folder_bulk`, payload);
  }

  //TODO remove any type
  getMove(id: number) {
    return this.http
      .get<
        {
          id: number;
          quan: number;
          price: number;
          price_current: number;
          import_doc: ImportDoc;
          export_doc: ExportDoc;
          return_to_warehouse_doc: ReturnToWarehouseDoc;
          return_to_provider_doc: ReturnToProviderDoc;
          moving_doc: MovingDoc;
          order: Order;
          product: Product;
        }[]
      >(`${environment.BACKEND}/product/move/${id}`)
      .pipe(
        map((log: any) => {
          return log.map((row: any) => {
            if (row.export_doc) {
              row.export_doc.date = new Date(row.export_doc.date);
              row.export_doc.date_created = new Date(row.export_doc.date_created);
            }
            if (row.import_doc) {
              row.import_doc.date = new Date(row.import_doc.date);
              row.import_doc.date_created = new Date(row.import_doc.date_created);
            }
            if (row.return_to_provider_doc) {
              row.return_to_provider_doc.date = new Date(row.return_to_provider_doc.date);
              row.return_to_provider_doc.date_created = new Date(
                row.return_to_provider_doc.date_created
              );
            }
            if (row.return_to_warehouse_doc) {
              row.return_to_warehouse_doc.date = new Date(row.return_to_warehouse_doc.date);
              row.return_to_warehouse_doc.date_created = new Date(
                row.return_to_warehouse_doc.date_created
              );
            }
            return row;
          });
        })
      );
  }

  getUnifiedMove(
    productId: number,
    payload: { limit?: number; offset?: number; eventTypes?: string[] } = {}
  ): Observable<UnifiedProductMovementResponse> {
    let params = new HttpParams();
    if (payload.limit !== undefined) {
      params = params.set('limit', String(payload.limit));
    }
    if (payload.offset !== undefined) {
      params = params.set('offset', String(payload.offset));
    }

    if (payload.eventTypes?.length) {
      params = params.set('eventTypes', payload.eventTypes.join(','));
    }

    return this.http
      .get<UnifiedProductMovementResponse>(`${environment.BACKEND}/product/move/v3/${productId}`, {
        params
      })
      .pipe(
        map((response) => ({
          ...response,
          data: response.data.map((row: UnifiedProductMovementItem) => ({
            ...row,
            date: new Date(row.date),
            quantity: Number(row.quantity),
            price: row.price === null || row.price === undefined ? null : Number(row.price)
          }))
        }))
      );
  }

  getOne(id: number): Observable<Product> {
    return this.http.get<Product>(`${environment.BACKEND}/product/${id}`);
  }

  importFromExist(
    productId: number,
    payload: ProductImportFromExistRequest = {}
  ): Observable<ProductImportFromExistResult> {
    return this.http.post<ProductImportFromExistResult>(
      `${environment.BACKEND}/product/${productId}/import_from_exist`,
      payload
    );
  }

  addAlternativeArticle(payload: ArticleCreateDto) {
    return this.http.post<Article>(`${environment.BACKEND}/article/create`, payload);
  }

  removeAlternativeArticle(id: number) {
    return this.http.delete<void>(`${environment.BACKEND}/article/${id}`);
  }

  updateAlternativeArticle(payload: ArticleUpdateDto) {
    return this.http.put<Article>(`${environment.BACKEND}/article/update`, payload);
  }

  searchAlternativeArticle(payload: ArticleSearchDto) {
    return this.http.post<{ data: Article[]; length: number }>(
      `${environment.BACKEND}/article/search`,
      payload
    );
  }

  uploadNewProducts(payload: UploadNewProductsFile) {
    const formData: FormData = new FormData();
    formData.set('file', payload.file);
    formData.set('description_column', String(payload.description_column));
    formData.set('article_column', String(payload.article_column));
    formData.set('brand_column', String(payload.brand_column));
    formData.set('image_column', String(payload.image_column));
    formData.set('folder_column', String(payload.folder_column));
    return this.http.post(`${environment.BACKEND}/product/upload_new_products`, formData);
  }

  getCountSearch(payload: ProductSearchDto) {
    return this.http.post<{
      length: number;
      min_retail: number;
      max_retail: number;
    }>(`${environment.BACKEND}/product/search_count`, payload);
  }

  getBrandListSearch(payload: ProductSearchDto) {
    return this.http.post<Brand[]>(`${environment.BACKEND}/product/search_brand_list`, payload);
  }

  getSearchKeys(id: number): Observable<SearchKey[]> {
    return this.http.get<SearchKey[]>(`${environment.BACKEND}/product/${id}/search_keys`);
  }

  addSearchKey(payload: ToggleSearchKey) {
    return this.http.put<void>(`${environment.BACKEND}/product/add_search_key`, payload);
  }

  removeSearchKey(payload: ToggleSearchKey) {
    return this.http.put<void>(`${environment.BACKEND}/product/remove_search_key`, payload);
  }

  updateRetail(payload: UpdateRetailPriceDto) {
    return this.http.put<Product>(`${environment.BACKEND}/product/update_retail`, payload);
  }

  updateAutoRetail(productId: number, updateRetail: boolean) {
    return this.updateProduct({ id: productId, updateRetail });
  }

  toggleProm(id: number) {
    return this.http.put(`${environment.BACKEND}/product/toggle_prom/${id}`, null);
  }

  toggleAvtopro(id: number) {
    return this.http.put(`${environment.BACKEND}/product/toggle_avtopro/${id}`, null);
  }

  togglePublic(id: number) {
    return this.http.put<Product>(`${environment.BACKEND}/product/toggle_public/${id}`, null);
  }
}
